/**
 * Viewer Navigation Utilities
 *
 * Provides utilities for navigating to the viewer from any route
 * and executing viewer actions (zoom, isolate, etc.) after arrival.
 *
 * This utility encapsulates:
 * 1. Route navigation (using navigation history system)
 * 2. Viewer type switching (3D, 2D, 360)
 * 3. Viewer readiness detection
 * 4. Action execution after viewer is ready
 *
 * @see src/store/modules/navigation-history.modules.js - Navigation system
 * @see src/views/MainView.vue - openViewPageWithType method
 */

import store from '@/store'
import router from '@/router'
import { isViewerRoute } from '@/constants/navigationTypes'
import Vue from 'vue'
import { log, warn } from '@/utils/debugLogger'

/**
 * Viewer type constants
 * Use these when specifying which viewer to navigate to
 */
export const VIEWER_TYPES = Object.freeze({
  MODEL_3D: '3d',
  PLAN_2D: '2d',
  PANORAMA_360: '360'
})

/**
 * Human-readable labels for viewer types, used in toast/warning messages.
 */
const VIEWER_TYPE_LABELS = {
  [VIEWER_TYPES.MODEL_3D]: '3D model',
  [VIEWER_TYPES.PLAN_2D]: '2D plan',
  [VIEWER_TYPES.PANORAMA_360]: '360 panorama'
}

// ─────────────────────────────────────────────────────────────
// Module-level state for watcher cleanup and concurrent call cancellation
// ─────────────────────────────────────────────────────────────

/**
 * Tracks active store.watch() subscriptions for cleanup on route navigation.
 * Each entry has a { cleanup } method that unsubscribes the watcher.
 */
const activeWatchers = new Set()

/**
 * Tracks the most recent pending navigation to support concurrent call cancellation.
 * When a new navigateToViewerWithAction call arrives while one is pending,
 * the previous one is cancelled so only the latest action executes.
 */
let pendingNavigation = null

/**
 * Cancel all pending viewer wait subscriptions.
 * Called by the router navigation guard when leaving viewer routes.
 * Prevents store.watch() subscriptions from lingering for the full safety timeout.
 *
 * Each watcher entry has:
 * - cleanup(): unsubscribes the watcher and clears timers
 * - resolve(null): settles the Promise so the await in navigateToViewerWithAction
 *   doesn't hang forever (orphaned Promise fix)
 *
 * @export
 */
export function cancelPendingViewerWaits() {
  if (activeWatchers.size > 0) {
    activeWatchers.forEach(w => {
      w.cleanup()
      // Settle the Promise so the caller's await doesn't hang forever
      if (typeof w.resolve === 'function') {
        w.resolve(null)
      }
    })
    activeWatchers.clear()
  }
}

/**
 * Navigate to viewer and execute action
 *
 * This is the main utility function for panels to call when they need
 * to perform a viewer action (zoom, isolate, etc.) but the user may
 * not be on the viewer route.
 *
 * Flow:
 * 1. Check if already on viewer route
 * 2. Navigate to viewer if needed (using GO_TO_VIEWER_SHORTCUT)
 * 3. Open the correct viewer type (3D, 2D, or 360)
 * 4. Wait for viewer to be ready
 * 5. Execute the action
 *
 * Concurrent calls: If called while a previous call is still waiting,
 * the previous call is cancelled and only the latest action executes.
 *
 * @param {string} viewerType - One of VIEWER_TYPES (e.g., 'MODEL_3D', 'PLAN_2D', 'PANORAMA_360')
 * @param {Function} action - The action to execute after navigation (e.g., () => forgeService.isolateTag(tag))
 * @param {Object} options - Configuration options
 * @param {boolean} options.waitForViewer - Whether to wait for viewer to be ready (default: true)
 * @param {number} options.timeout - Max wait time for viewer in ms (default: 3000, used for 2D/360 only)
 * @param {boolean} options.skipIfOnViewer - Skip navigation if already on viewer (default: true)
 *
 * @returns {Promise<any>} Result of the action function, or null if aborted/cancelled
 *
 * @example
 * // Navigate to 3D viewer and isolate tag
 * await navigateToViewerWithAction(
 *   VIEWER_TYPES.MODEL_3D,
 *   () => this.forgeService.isolateTag(this.tag, true)
 * )
 *
 * @example
 * // Navigate to 2D viewer and zoom to level
 * await navigateToViewerWithAction(
 *   VIEWER_TYPES.PLAN_2D,
 *   () => this.forgeService.zoomToPoint2D(point, levelName)
 * )
 */
export async function navigateToViewerWithAction(viewerType, action, options = {}) {
  // Resolve viewer type first — needed for computing smart defaults below
  const resolvedViewerType = VIEWER_TYPES[viewerType] || viewerType

  const {
    // 360 viewer is on-demand (created per-image by the action itself),
    // so waiting for it before running the action would deadlock.
    waitForViewer = resolvedViewerType !== VIEWER_TYPES.PANORAMA_360,
    timeout = 3000,
    skipIfOnViewer = true,
    // Opt-in per caller (v1: InspectionFiltersDrawer). When a real 3D canvas is already on
    // screen (dashboard embedded viewer OR MainView), apply the action there instead of
    // navigating to MainView. See INSPECTION_FILTER_IN_PLACE_VIEWER_ACTIONS.md.
    allowInPlace = false
  } = options

  // Cancel any previous pending navigation
  if (pendingNavigation?.cancel) {
    pendingNavigation.cancel()
  }

  let isCancelled = false
  pendingNavigation = { cancel: () => { isCancelled = true } }

  // In-place fast path: a real 3D canvas is already on screen (dashboard embedded viewer OR
  // MainView). v1: 3D only (the only opt-in caller). READY-NOW — we never wait here, so a user
  // who leaves mid-action can't strand a delayed closure. Skips navigation AND
  // openViewPageWithType below; the isCancelled checkpoints still bracket the action.
  const inPlace = allowInPlace
    && resolvedViewerType === VIEWER_TYPES.MODEL_3D
    && isViewer3DUsableInPlace()
  log('[ViewerNav]', inPlace ? 'apply IN PLACE (on-screen 3D viewer, no navigation)' : 'apply after navigation')

  // NOTE: the in-place path deliberately does NOT try to single-flight rapid re-triggers. These
  // actions (colour / isolate / markers) are idempotent and latest-wins — a rapid on→off ends at
  // off, which is correct — and they apply immediately, so two sequential synchronous calls can't
  // overlap and can't be cancelled. The navigate path keeps its pendingNavigation single-flight
  // (which matters there because navigation opens a long async window). See spec FR4.

  // 1. Check current route
  const currentRoute = router.currentRoute
  const isOnViewer = isViewerRoute(currentRoute.name)

  // 2. Navigate to viewer if needed (skipped for the in-place fast path)
  if (!inPlace && !isOnViewer) {
    try {
      await store.dispatch('NAVIGATION_HISTORY/GO_TO_VIEWER_SHORTCUT')

      // Wait for route change to complete
      await Vue.nextTick()

      // Additional wait for DOM updates
      await new Promise(resolve => setTimeout(resolve, 100))
    } catch (err) {
      warn('[ViewerNav]', 'Navigation failed', { error: err.message })
      // Continue anyway - user might already be on viewer
    }
  }

  if (isCancelled) return null

  // 3. Wait for MainView to mount (if we just navigated) and open correct viewer type.
  //    Skipped for the in-place fast path — the on-screen viewer needs no MainView handoff.
  if (!inPlace) {
    const mainApp = !isOnViewer
      ? await waitForMainApp(timeout)  // Wait for MainView to mount after navigation
      : store.state.mainApp            // Already on viewer, mainApp should exist

    if (mainApp && typeof mainApp.openViewPageWithType === 'function') {
      mainApp.openViewPageWithType(resolvedViewerType)
      await Vue.nextTick()
    } else {
      warn('[ViewerNav]', 'MainView not available after waiting')
    }
  }

  if (isCancelled) return null

  // 4. Wait for viewer to be ready (if requested). Skipped in-place — detection already
  //    confirmed the viewer is ready+attached NOW (ready-now semantics).
  if (!inPlace && waitForViewer) {
    const viewer = await waitForViewerReady(resolvedViewerType, timeout)
    if (!viewer) {
      const label = VIEWER_TYPE_LABELS[resolvedViewerType] || 'viewer'
      Vue.prototype.$dialog?.notify?.warning?.(
        `Please wait for the ${label} to finish loading.`,
        { position: 'top-right', timeout: 3000 }
      )
      return null
    }
  }

  if (isCancelled) return null

  // 5. Execute the action
  if (typeof action === 'function') {
    try {
      const result = await action()
      return result
    } catch (err) {
      warn('[ViewerNav]', 'Action failed', { error: err?.message || err })
      return null
    } finally {
      pendingNavigation = null
    }
  }

  pendingNavigation = null
  return null
}

/**
 * Wait for a specific viewer to be ready
 *
 * For 3D viewer: uses event-driven Vuex store.watch() (handles 2+ min load times).
 * For 2D/360: uses polling with exponential backoff (loads are fast).
 *
 * @param {string} viewerType - The viewer type ('3d', '2d', '360')
 * @param {number} timeout - Maximum wait time in milliseconds (used for 2D/360 only)
 * @returns {Promise<Object|null>} The viewer instance or null if timeout
 */
async function waitForViewerReady(viewerType, timeout = 3000) {
  // For 3D viewer: use event-driven waiting (handles 2+ min load times)
  if (viewerType === VIEWER_TYPES.MODEL_3D) {
    return waitForViewer3DReady()
  }

  // For 2D/360: keep polling approach (loads are fast)
  const startTime = Date.now()
  let checkInterval = 50 // Start with 50ms, increase over time

  return new Promise((resolve) => {
    const checkViewer = () => {
      const viewer = getViewerByType(viewerType)

      if (viewer && isViewerReady(viewer, viewerType)) {
        resolve(viewer)
        return
      }

      if (Date.now() - startTime > timeout) {
        resolve(null)
        return
      }

      // Exponential backoff (cap at 200ms)
      checkInterval = Math.min(checkInterval * 1.5, 200)
      setTimeout(checkViewer, checkInterval)
    }

    checkViewer()
  })
}

/**
 * Event-driven wait for 3D viewer readiness.
 *
 * Uses Vuex store.watch() to resolve the instant geometry + tree are loaded.
 * No polling — reacts to Vue's reactivity system.
 * Safety timeout prevents indefinite wait on failed loads and cleans up
 * the store.watch() subscription if the user navigates away.
 *
 * The watcher is tracked in `activeWatchers` so it can be cleaned up
 * immediately by `cancelPendingViewerWaits()` on route navigation.
 *
 * @param {number} safetyTimeout - Max wait in ms (default: 180000 = 3 min)
 * @returns {Promise<Object|null>} The viewer instance or null on safety timeout
 */
async function waitForViewer3DReady(safetyTimeout = 180000) {
  return new Promise((resolve) => {
    // 1. Check if already ready (viewer was previously loaded)
    if (store.getters.isViewer3DReady) {
      resolve(store.state.viewer)
      return
    }

    let resolved = false
    let safetyTimer = null
    let unwatch = null

    const cleanup = () => {
      resolved = true
      if (safetyTimer) clearTimeout(safetyTimer)
      if (unwatch) unwatch()
      activeWatchers.delete(watcherEntry)
    }

    // Track this watcher for external cleanup (route navigation guard)
    // Expose both cleanup() and resolve() so cancelPendingViewerWaits can:
    //   1. Unsubscribe the watcher (cleanup)
    //   2. Settle the Promise (resolve) to prevent orphaned awaits
    const watcherEntry = { cleanup, resolve }
    activeWatchers.add(watcherEntry)

    // 2. Watch store reactively — resolves the instant flags become true
    unwatch = store.watch(
      (state, getters) => getters.isViewer3DReady,
      (ready) => {
        if (resolved) return
        if (ready) {
          cleanup()
          resolve(store.state.viewer)
        }
      }
    )

    // 3. Safety timeout — prevents indefinite wait if model load fails
    //    or cleans up if user navigates away
    safetyTimer = setTimeout(() => {
      if (!resolved) {
        warn('[ViewerNav]', '3D viewer safety timeout reached', {
          safetyTimeout,
          hasViewer: !!store.state.viewer,
          isGeometryLoaded: store.state.isGeometryLoaded,
          isTreeLoaded: store.state.isTreeLoaded
        })
        cleanup()
        resolve(null)
      }
    }, safetyTimeout)
  })
}

/**
 * Wait for MainView to mount and set mainApp reference
 *
 * When navigating from non-viewer routes, MainView.vue needs time to mount
 * and register itself in the store. This function polls until mainApp is
 * available or timeout is reached.
 *
 * @param {number} timeout - Maximum wait time in milliseconds
 * @returns {Promise<Object|null>} The mainApp instance or null if timeout
 */
async function waitForMainApp(timeout = 3000) {
  const startTime = Date.now()
  let checkInterval = 50

  return new Promise((resolve) => {
    const checkMainApp = () => {
      const mainApp = store.state.mainApp

      if (mainApp && typeof mainApp.openViewPageWithType === 'function') {
        resolve(mainApp)
        return
      }

      if (Date.now() - startTime > timeout) {
        resolve(null)
        return
      }

      // Exponential backoff (cap at 200ms)
      checkInterval = Math.min(checkInterval * 1.5, 200)
      setTimeout(checkMainApp, checkInterval)
    }

    checkMainApp()
  })
}

/**
 * Check if a viewer is ready for interaction
 *
 * @param {Object} viewer - The viewer instance
 * @param {string} viewerType - The viewer type
 * @returns {boolean} True if viewer is ready
 */
function isViewerReady(viewer, viewerType) {
  if (!viewer) return false

  switch (viewerType) {
    case VIEWER_TYPES.MODEL_3D:
      return store.getters.isViewer3DReady

    case VIEWER_TYPES.PLAN_2D:
      // 2D viewer is ready when it exists
      return !!viewer

    case VIEWER_TYPES.PANORAMA_360:
      // 360 viewer is ready when it exists
      return !!viewer

    default:
      return !!viewer
  }
}

/**
 * Get viewer instance by type
 *
 * @param {string} viewerType - The viewer type ('3d', '2d', '360')
 * @returns {Object|null} The viewer instance or null
 */
function getViewerByType(viewerType) {
  switch (viewerType) {
    case VIEWER_TYPES.MODEL_3D:
      return store.state.viewer

    case VIEWER_TYPES.PLAN_2D:
      return store.state.planViewer

    case VIEWER_TYPES.PANORAMA_360:
      return store.state.forgeService?.panoramaViewer

    default:
      warn('[ViewerNav]', 'Unknown viewer type', { viewerType })
      return null
  }
}

/**
 * Check if a specific viewer type is currently visible
 *
 * @param {string} viewerType - The viewer type ('3d', '2d', '360')
 * @returns {boolean} True if the viewer is visible
 */
export function isViewerTypeVisible(viewerType) {
  const viewersState = store.state.Viewers

  if (!viewersState) return false

  const { view1Type, view2Type, view3Type, view4Type,
          isView1Visible, isView2Visible, isView3Visible, isView4Visible } = viewersState

  return (
    (isView1Visible && view1Type === viewerType) ||
    (isView2Visible && view2Type === viewerType) ||
    (isView3Visible && view3Type === viewerType) ||
    (isView4Visible && view4Type === viewerType)
  )
}

/**
 * Is a 3D viewer usable IN PLACE right now — i.e. is there a real, on-screen 3D canvas the
 * user can see, without navigating? Uses DOM ground truth (not route/store flags), so it
 * distinguishes:
 *   - MainView-attached OR embedded-ViewersSection-attached  -> usable (true)
 *   - detached "zombie" (loaded on MainView earlier, never adopted here) -> NOT usable
 *   - hidden 2D/360 tab (v-show) / closed section (v-if)      -> NOT usable (offsetParent null)
 *
 * offsetParent === null means display:none (a hidden v-show tab) OR detached — the right test
 * for the Forge canvas host (a normal, non-fixed div). If that host ever becomes position:fixed,
 * switch to `el.getClientRects().length > 0`.
 *
 * ⚠️ INTERLOCK: relies on the shared, store-owned viewer instance persisting across route
 * changes (ViewersSection does NOT dispose it on unmount — VIEWERS_SECTION_AUDIT.md Cluster 1).
 * If that leak is ever fixed, in-place application AND cross-view coloring persistence break.
 * See .claude/features/INSPECTION_FILTER_IN_PLACE_VIEWER_ACTIONS.md.
 *
 * @returns {boolean}
 */
export function isViewer3DUsableInPlace() {
  if (!store.getters.isViewer3DReady) return false
  const el = store.state.viewer?.container
  return !!el && el.isConnected && el.offsetParent !== null
}

/**
 * Navigate to viewer route only (without opening specific viewer type)
 *
 * Use this when you just need to ensure user is on the viewer route
 * but don't need to control which viewer type is open.
 *
 * @returns {Promise<boolean>} True if navigation succeeded or already on viewer
 */
export async function ensureOnViewerRoute() {
  const currentRoute = router.currentRoute
  const isOnViewer = isViewerRoute(currentRoute.name)

  if (isOnViewer) return true

  try {
    await store.dispatch('NAVIGATION_HISTORY/GO_TO_VIEWER_SHORTCUT')
    await Vue.nextTick()
    return true
  } catch (err) {
    warn('[ViewerNav]', 'Failed to navigate to viewer', { error: err.message })
    return false
  }
}

// Named export for waitForMainApp (used by components that need direct access)
export { waitForMainApp }

export default {
  VIEWER_TYPES,
  navigateToViewerWithAction,
  isViewerTypeVisible,
  isViewer3DUsableInPlace,
  ensureOnViewerRoute,
  waitForMainApp,
  cancelPendingViewerWaits
}
